import React from 'react';
import tw from 'twin.macro';
import { Tag } from 'components/Tag';
import { Color } from 'types/colors';
import { UsersIcon } from '@heroicons/react/24/outline';
import { Icon } from 'components/Icons/icon';
import Popover from 'components/Popover';
import { BasicEleveInfo } from 'types/eleveGroups';
import { useTranslation } from 'react-i18next';
import { TagColorWithIntensity } from '@teetsh/app/src/styles/userConstants';

interface Props {
  color: Color | TagColorWithIntensity;
  groupName?: string;
  interactive?: boolean;
  icon?: Icon;
  className?: string;
  withoutIcon?: boolean;
  'data-testid'?: string;
  withElevesDetails?: boolean;
  groupEleves?: BasicEleveInfo[];
  isEphemeral?: boolean;
  print?: boolean;
}

interface ElevesDetailsPanelProps {
  groupEleves: BasicEleveInfo[];
}

function ElevesDetailsPanel({ groupEleves }: ElevesDetailsPanelProps) {
  const { t } = useTranslation(['eleve', 'cahierJournal', 'common']);
  return (
    <div tw="p-3 max-h-64 overflow-y-auto">
      <div tw="text-xs font-semibold mb-2 text-neutral-moderate">
        {t('eleve:xEleve', { count: groupEleves.length })}
      </div>
      <div tw="space-y-1">
        {groupEleves.map((eleve: BasicEleveInfo) => (
          <div key={eleve.id} tw="text-sm">
            {eleve.givenName} {eleve.familyName}
          </div>
        ))}
      </div>
    </div>
  );
}

export function GroupTag(props: Props) {
  const {
    interactive,
    color,
    groupName,
    className,
    withElevesDetails,
    groupEleves,
    isEphemeral,
    print,
  } = props;
  const IconElement: any = props.icon || UsersIcon;

  // For ephemeral groups, display student names instead of group name
  const displayName =
    isEphemeral && groupEleves
      ? groupEleves.map(e => e.givenName).join(', ')
      : groupName;

  const tagElement = (
    <Tag
      className={className}
      data-testid={props['data-testid']}
      interactive={interactive}
      color={color}
      tw="rounded-md"
    >
      <div tw="flex items-start gap-x-2">
        {IconElement && !props.withoutIcon && (
          <IconElement tw="h-4 size-3.5 shrink-0" />
        )}
        <div tw="inline-flex flex-wrap gap-x-2">
          {displayName && (
            <span css={isEphemeral && !print && tw`text-wrap`}>
              {displayName}
            </span>
          )}
        </div>
      </div>
    </Tag>
  );

  if (!withElevesDetails || !groupEleves) {
    return tagElement;
  }

  return (
    <Popover portal placement="bottom-start">
      <Popover.Span>{tagElement}</Popover.Span>
      <Popover.Panel size="sm">
        <ElevesDetailsPanel groupEleves={groupEleves} />
      </Popover.Panel>
    </Popover>
  );
}
